import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@repo/api-client";
import type { Post } from "@repo/shared-types";

export function ContentPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [articleLoading, setArticleLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState(initialQ);
  const [submittedSearch, setSubmittedSearch] = useState(initialQ);
  const [activeTab, setActiveTab] = useState("All");

  useEffect(() => {
    if (slug) return;

    setLoading(true);
    setError("");
    api.listPosts({ per_page: "12", search: submittedSearch || undefined })
      .then((res) => setPosts(res.posts))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [submittedSearch, slug]);

  useEffect(() => {
    if (!slug) {
      setSelectedPost(null);
      return;
    }

    setArticleLoading(true);
    setError("");
    api.getPost(slug)
      .then((post) => setSelectedPost(post))
      .catch((err) => {
        setSelectedPost(null);
        setError(err instanceof Error ? err.message : "Failed to load article");
      })
      .finally(() => setArticleLoading(false));
  }, [slug]);

  function handleSearchSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmittedSearch(search.trim());
  }

  function renderExcerpt(post: Post): string {
    if (post.excerpt?.rendered) return post.excerpt.rendered;
    if (post.content?.rendered) return post.content.rendered;
    return "";
  }

  if (slug) {
    return (
      <>
        <section className="page-hero">
          <div className="container container--reading">
            <Link to="/wisdom" className="post-detail__back-link">← Back to Wisdom Library</Link>
            {articleLoading ? (
              <div className="page-loading">Loading article…</div>
            ) : error ? (
              <div className="card post-detail__error-card">
                <h2>Article unavailable</h2>
                <p className="text-muted">{error}</p>
                <Link to="/wisdom" className="btn btn--outline">Browse other articles</Link>
              </div>
            ) : selectedPost ? (
              <article className="post-detail">
                <div className="post-detail__meta">
                  <span className="post-card__category">Wisdom</span>
                  <time className="post-card__date">
                    {new Date(selectedPost.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                  </time>
                </div>
                <h1 className="display-3 post-detail__title">{stripHtml(selectedPost.title.rendered)}</h1>
                <div
                  className="post-detail__content"
                  dangerouslySetInnerHTML={{ __html: sanitizeWpHtml(selectedPost.content?.rendered || selectedPost.excerpt?.rendered || "") }}
                />
              </article>
            ) : null}
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      {/* ── Page Hero ─────────────────────────────────────────── */}
      <section className="page-hero">
        <div className="container container--wide content-hero-grid">
          <div className="content-hero-main">
            <span className="eyebrow">Wisdom Library</span>
            <h1 className="display-2 page-hero__title-spaced">
              Teachings for your journey
            </h1>
            <p className="body-lg text-muted content-hero__body">
              Explore a curated stream of spiritual teachings, guided meditations, and reflective articles
              synced from our content library and structured for daily practice.
            </p>

            {/* Search */}
            <form onSubmit={handleSearchSubmit} className="search-bar content-search-bar">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search teachings, meditations, articles…"
                className="form-input"
              />
              <button type="submit" className="btn btn--primary">Search</button>
              {submittedSearch && (
                <button
                  type="button"
                  className="btn btn--outline"
                  onClick={() => { setSearch(""); setSubmittedSearch(""); }}
                >
                  Clear
                </button>
              )}
            </form>
          </div>

          <aside className="content-hero-side" aria-label="Library highlights">
            <div className="content-hero-side__stat">
              <span className="content-hero-side__value">{posts.length || "12+"}</span>
              <span className="content-hero-side__label">Articles surfaced</span>
            </div>
            <div className="content-hero-side__list">
              {[
                "Daily meditation sequences",
                "Dream interpretation frameworks",
                "Mindfulness for modern routines",
                "Community reflection stories",
              ].map((item) => (
                <div key={item} className="content-hero-side__item">{item}</div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {/* ── Category tabs ─────────────────────────────────────── */}
      <section className="section section--sm section--surface section-with-divider">
        <div className="container container--wide">
          <div className="tab-nav">
            {["All", "Meditation", "Philosophy", "Dream Wisdom", "Mindfulness", "Yoga"].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`tab-btn${activeTab === tab ? " active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Category cards */}
          <div className="grid-4 content-category-grid">
            {[
              { icon: "🕉️", title: "Teachings",         note: "Ancient frameworks for modern life" },
              { icon: "🧘", title: "Meditation Guides",  note: "Breath-led clarity and stillness" },
              { icon: "💬", title: "Community Stories",  note: "Real seeker growth patterns" },
              { icon: "📖", title: "Courses",            note: "Structured long-form journeys" },
            ].map((item) => (
              <button
                key={item.title}
                type="button"
                onClick={() => setActiveTab(item.title === "Teachings" ? "Philosophy" : item.title === "Meditation Guides" ? "Meditation" : "All")}
                className="content-category-card"
              >
                <div className="content-category-card__icon">{item.icon}</div>
                <div className="content-category-card__title">{item.title}</div>
                <div className="content-category-card__note">{item.note}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Posts grid ────────────────────────────────────────── */}
      <section className="section section--surface-alt">
        <div className="container container--wide">
          {error && <p className="alert alert--error alert--spaced">{error}</p>}

          {loading ? (
            <div className="page-loading">Loading wisdom library…</div>
          ) : posts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state__icon">🕉️</div>
              <h3 className="heading-lg empty-state__title">No content found</h3>
              <p className="text-muted body-sm">
                {submittedSearch ? `No results for "${submittedSearch}". Try a different search.` : "Check back later for new wisdom."}
              </p>
              {submittedSearch && (
                <button
                  type="button"
                  className="btn btn--outline empty-state__action"
                  onClick={() => { setSearch(""); setSubmittedSearch(""); }}
                >
                  Clear search
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-muted body-sm section-count-row">
                {posts.length} article{posts.length !== 1 ? "s" : ""} found
                {submittedSearch ? ` for "${submittedSearch}"` : ""}
              </p>
              <div className="grid-auto">
                {posts.map((post, idx) => (
                  <article key={post.id} className="post-card">
                    <div className="post-card__image-stub">
                      {["📖","🧘","✨","🕉️","📿","🌿","🔮","🪷"][idx % 8]}
                    </div>
                    <div className="post-card__body">
                      <div className="post-card__meta">
                        <span className="post-card__category">{activeTab === "All" ? "Wisdom" : activeTab}</span>
                        <time className="post-card__date">
                          {new Date(post.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                        </time>
                      </div>
                      <h3 className="post-card__title">{stripHtml(post.title.rendered)}</h3>
                      <p className="post-card__excerpt">
                        {stripHtml(renderExcerpt(post)).slice(0, 200)}
                      </p>
                      <div className="post-card__footer">
                        <Link to={`/wisdom/${post.slug}`} className="post-card__readmore">Read article →</Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Newsletter CTA ────────────────────────────────────── */}
      <section className="newsletter-band">
        <div className="container content-newsletter-wrap">
          <span className="eyebrow content-newsletter-eyebrow">Stay inspired</span>
          <h2 className="display-3 content-newsletter-title">Weekly wisdom in your inbox</h2>
          <p className="body-lg text-muted content-newsletter-body">
            Get fresh teachings, meditation guidance, and curated insights delivered every week.
          </p>
          <div className="newsletter-form content-newsletter-form">
            <input type="email" placeholder="Your email address" className="form-input content-newsletter-input" />
            <button type="button" className="btn btn--primary">Subscribe free</button>
          </div>
        </div>
      </section>
    </>
  );
}

function stripHtml(input: string): string {
  return input.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function sanitizeWpHtml(input: string): string {
  return input
    .replace(/<(script|style|iframe|object|embed|link|meta)[^>]*>[\s\S]*?<\/\1>/gi, "")
    .replace(/<(script|style|iframe|object|embed|link|meta)[^>]*\/?\s*>/gi, "")
    .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son[a-z]+\s*=\s*'[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}
